import type { PersistedShipment } from '../../../lib/planningData';
import type { InternalUserSchedule } from '../../Scenario';
import type { ClientId } from '../../core/ClientId';
import { migrateScenarioAddPatchShipments } from './20240227_addPatchShipments';
import { migrateScenarioDeleteEventIds } from './20221208_deleteEventIds';
import migrateTmpShipmentIds from './tmpShipmentId';
import { migrateScenarioPatchShipmentsClientIds } from './20240313_patchShipmentsClientIds';
import { migrateScenarioAddFreightByShipmentClientId } from './20250114_addFreightByShipmentClientId';
import { LATEST_MIGRATION_VERSION } from '@seaber/common/core/scenario';
export { LATEST_MIGRATION_VERSION } from '@seaber/common/core/scenario';

export type ScenarioMigration = ((
  scenario: InternalUserSchedule,
  migrateData: MigrateData,
) => InternalUserSchedule) & {
  migrateAlwaysIf?: (scenario: InternalUserSchedule) => boolean;
};

/** All frontend scenario migrations in order of introducing. If you make a migration, add it to the end of the list.
 * DO NOT add migrations anywhere other than at the end. */
const migrations: ScenarioMigration[] = [
  migrateTmpShipmentIds,
  migrateScenarioDeleteEventIds,
  migrateScenarioAddPatchShipments,
  migrateScenarioPatchShipmentsClientIds,
  migrateScenarioAddFreightByShipmentClientId,
  // Add new migration above this line
];

// If this doesn't match, update the imported value in source
if (migrations.length !== LATEST_MIGRATION_VERSION) {
  throw new Error(
    `Mismatch between migrations count and latest migration version in core`,
  );
}

export type MigrateData = {
  persistedShipmentByClientId: Map<ClientId, PersistedShipment>;
};

/** Migrate frontend scenario schema. Call this on any scenario loaded from browser or db store. */
export function migrateAll(
  scenario: InternalUserSchedule,
  migrateData: MigrateData,
): InternalUserSchedule {
  for (
    let currentVersion = 1;
    currentVersion <= migrations.length;
    ++currentVersion
  ) {
    const scenarioMigrationVersion = scenario.migrationVersion || 0;
    const migration = migrations[currentVersion - 1];
    if (scenarioMigrationVersion < currentVersion) {
      scenario = migration(scenario, migrateData);
      scenario.migrationVersion = currentVersion;
    } else if (migration.migrateAlwaysIf?.(scenario)) {
      // There has been a bug where numerical shipmentIds have been generated into scenarios even after migrations had been introduced (IMOS scenario generation).
      // We allow the tmp shipment id patch migration to be run anyway even if migration version says it's already handled.
      console.warn(
        `Migration ${currentVersion} had already been applied but requires re-applying for scenario:`,
        scenario,
      );
      scenario = migration(scenario, migrateData);
    }
  }
  return scenario;
}
